import { useMemo } from "react";
import { toast } from "sonner";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { getStripe } from "@/lib/stripe";
import { createCheckoutSession, type CreateCheckoutSessionInput } from "@/lib/payments.functions";

/**
 * Renders Stripe's embedded checkout for a fixed set of session params.
 * `params` must be referentially stable while mounted — the parent owns
 * the "which checkout is open" state and only creates a new `params`
 * object when starting a brand-new checkout, never on every render.
 */
export function StripeEmbeddedCheckout({ params }: { params: CreateCheckoutSessionInput }) {
  const stripePromise = useMemo(() => getStripe(), []);

  const options = useMemo(
    () => ({
      fetchClientSecret: async () => {
        try {
          const result = await createCheckoutSession({ data: params });
          if ("error" in result) throw new Error(result.error);
          return result.clientSecret;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Checkout could not be opened.";
          toast.error(message);
          throw err;
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(params)],
  );

  return (
    <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
      <EmbeddedCheckout />
    </EmbeddedCheckoutProvider>
  );
}
